import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { adminApi, type PipelineEvent } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { PageHeader } from "../components/layout";
import { EventLevelBadge, StatusBadge } from "../components/status-badge";
import { JsonBlock } from "../components/json-block";

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
}

function EventRow({ event }: { event: PipelineEvent }) {
  const [open, setOpen] = useState(event.level === "ERROR");

  return (
    <div className="relative pl-7">
      <span
        className={`absolute left-0 top-2 h-3 w-3 rounded-full border-2 bg-white ${
          event.level === "ERROR"
            ? "border-danger"
            : event.level === "WARN"
              ? "border-warning"
              : "border-primary"
        }`}
      />
      <div className="panel overflow-hidden">
        <div
          className="flex w-full items-start gap-3 p-4 text-left"
        >
          <button
            className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-white text-muted-foreground transition-colors hover:border-primary/40 hover:bg-accent hover:text-accent-foreground"
            type="button"
            aria-label={open ? "Свернуть событие" : "Раскрыть событие"}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <EventLevelBadge level={event.level} />
              <span className="text-xs text-muted-foreground">{formatDate(event.createdAt)}</span>
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{event.step}</span>
            </span>
            <span className="mt-2 block font-medium">{event.title}</span>
            <span className="mt-1 block text-sm text-muted-foreground">{event.summary}</span>
          </span>
        </div>

        {open ? (
          <div className="space-y-3 border-t p-4">
            <div>
              <p className="mb-2 text-sm font-medium">Details</p>
              <JsonBlock value={event.details} />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Raw</p>
              <JsonBlock value={event.raw} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function RunPage() {
  const { id } = useParams();
  const { token } = useAdminAuth();

  const run = useQuery({
    queryKey: ["admin", "run", id],
    queryFn: () => adminApi.run(token!, id!),
    enabled: Boolean(token && id),
    refetchInterval: (query) => (query.state.data?.status === "RUNNING" ? 3000 : false),
  });

  if (run.isLoading) {
    return <div className="panel p-4 text-sm text-muted-foreground">Загружаем run...</div>;
  }

  if (run.error || !run.data) {
    return <div className="panel p-4 text-sm text-danger">{run.error?.message ?? "Run не найден"}</div>;
  }

  return (
    <div>
      <PageHeader
        title={`Run ${run.data.id.slice(0, 8)}`}
        description={`${run.data.funnel.name} · ${run.data.account.amoAccountId}`}
        backTo={`/funnels/${run.data.funnelId}`}
        backLabel="К агенту"
      />

      <section className="panel mb-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={run.data.status} />
          <span className="text-sm text-muted-foreground">{formatDate(run.data.createdAt)}</span>
          {run.data.errorStep ? (
            <span className="rounded-md border border-danger/20 bg-danger/10 px-2 py-0.5 text-xs text-danger">
              {run.data.errorStep}
            </span>
          ) : null}
        </div>

        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <p className="text-muted-foreground">chatId</p>
            <p className="break-all font-medium">{run.data.chatId}</p>
          </div>
          <div>
            <p className="text-muted-foreground">channelId</p>
            <p className="break-all font-medium">{run.data.channelId}</p>
          </div>
          <div>
            <p className="text-muted-foreground">messageId</p>
            <p className="break-all font-medium">{run.data.wazzupMessageId}</p>
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1 text-sm font-medium">Входящее сообщение</p>
          <div className="rounded-md border bg-muted p-3 text-sm">{run.data.inboundText}</div>
        </div>
      </section>

      <div className="relative space-y-3 before:absolute before:left-[5px] before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
        {run.data.events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}
